import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { castArray } from 'lodash';
import { ProfileUser } from 'lib/types/users';
import { PostSearchCriteria, FilterResult, PostSort } from 'lib/types/filters';
import {
  basicPostSortOptions,
  postSortOptions,
  queryToCriteria, queryToFilters,
} from 'lib/utils/filterUtils';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { ProfileMultiFilter } from 'components/MultiFilters/ProfileMultiFilter';
import { SortFilter } from 'components/Filters/SortFilter';
import { NetworkFilter } from 'components/Filters/NetworkFilter';
import { FileTypeFilter } from 'components/Filters/FileTypeFilter';
import { MoreFilter } from 'components/Filters/MoreFilter';
import { UserListFilter } from 'components/Filters/UserListFilter';
import { TimeframeOptionsFilter } from 'components/Filters/TimeframeOptionsFilter';
import { defaultTimeFrameOptions } from 'components/Filters/utils';
import { useTeams } from 'lib/contexts/teamsContext';
import { userIsPaidTeamMember } from 'lib/helpers';

interface Props {
  profileUser: ProfileUser;
  onChange: (criteria: PostSearchCriteria) => void;
}

export const FilterCriteria = ({ profileUser, onChange }: Props) => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const [criteria, setCriteria] = useState<PostSearchCriteria|undefined>();
  const [filters, setFilters] = useState<(FilterResult|string)[]>([]);
  const teams = useTeams();

  useEffect(() => {
    if (criteria) {
      onChange(criteria);
    }
  }, [currentUser, criteria]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    setFilters(queryToFilters(router.query));
    setCriteria(queryToCriteria(router.query));
  }, [router]);

  if (!criteria) return null;

  const multiFilterLabel = `Search @collaborators, @accounts, #tags, events, keywords, @${profileUser.username}'s roles`;

  return (
    <div className="flex flex-wrap">
      <div className="w-full grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <div className="col-span-1 md:col-span-3 lg:col-span-1 flex">
          <div className="w-full">
            <UserListFilter />
          </div>
        </div>
        <div className="col-span-2 flex">
          <div className="w-full">
            <ProfileMultiFilter
              filters={filters}
              profileUser={profileUser}
              label={multiFilterLabel}
              useNaturalLanguage
            />
          </div>
        </div>
        <div className="col-span-1">
          <NetworkFilter
            networks={criteria.networks as string[]}
            includeSnapchat={userIsPaidTeamMember(currentUser, teams)}
          />
        </div>
        <div className="col-span-1">
          <FileTypeFilter
            fileTypes={criteria.fileTypes as string[]}
            includeStories={userIsPaidTeamMember(currentUser, teams)}
          />
        </div>
        <div className="col-span-1">
          <TimeframeOptionsFilter
            minPostedAt={criteria.minPostedAt}
            maxPostedAt={criteria.maxPostedAt}
            options={defaultTimeFrameOptions}
          />
        </div>
        <div className="col-span-1">
          <MoreFilter
            engagement={{
              ...(criteria.minViews && { minViews: criteria.minViews }),
              ...(criteria.maxViews && { maxViews: criteria.maxViews }),
              ...(criteria.minLikes && { minLikes: criteria.minLikes }),
              ...(criteria.maxLikes && { maxLikes: criteria.maxLikes }),
              ...(criteria.minShares && { minShares: criteria.minShares }),
              ...(criteria.maxShares && { maxShares: criteria.maxShares }),
              ...(criteria.minComments && { minComments: criteria.minComments }),
              ...(criteria.maxComments && { maxComments: criteria.maxComments }),
            }}
            includeUncredited={criteria.includeUncredited}
            roleTagSlugs={router.query.roleTags ? castArray(router.query.roleTags) : []}
          />
        </div>
        <div className="col-span-2 lg:col-span-1 flex justify-end items-center">
          <SortFilter<PostSort>
            sortOptions={profileUser?.id === currentUser?.id
              ? postSortOptions
              : basicPostSortOptions}
            sort={criteria.sort}
            defaultSort="postedAt"
          />
        </div>
      </div>
    </div>
  );
};
